import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/simpro-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | simPRO Alternative for Small Builders",
  "description": "Looking for a simPRO alternative for small UK builders? The Proper Tool is a leaner quoting-only option: AI-powered, UK-built, from £29/mo with a 14-day free trial.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "simPRO Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best simPRO alternative for small builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For small UK builders whose main need is fast, accurate construction quoting, The Proper Tool is the strongest simPRO alternative. simPRO is a full field service management platform designed for teams of 10+ with complex scheduling and inventory needs. The Proper Tool focuses specifically on construction quoting: AI-generated scopes from drawings or descriptions, a 31-category UK cost library, and a 14-day free trial from £29/month. It is faster to set up and costs a fraction of simPRO.",
      },
    },
    {
      "@type": "Question",
      "name": "Is simPRO good for residential builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "simPRO is primarily designed for field service businesses: electrical, plumbing, HVAC, and similar trades with recurring service contracts and multiple technicians. For residential builders doing extension and alteration work, it is generally more software than the job requires. Its quoting module is built around service job templates rather than structured construction scopes of works, and its pricing database is not calibrated for UK residential construction rates.",
      },
    },
    {
      "@type": "Question",
      "name": "How does simPRO pricing compare to The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "simPRO does not publish a standard UK price. It asks prospective customers to request pricing and offers a guided, personalised demo rather than a self-serve trial. The Proper Tool starts at £29 per month with a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "Can simPRO read construction drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "simPRO's optional Takeoffs add-on supports plan upload, manual measurement and markup, plus symbol counting or recognition. This is partial drawing assistance, not full autonomous drawing understanding. The Proper Tool reads uploaded PDF drawings using AI.",
      },
    },
    {
      "@type": "Question",
      "name": "How long does it take to set up simPRO vs The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "simPRO typically requires a formal implementation process that can take several weeks to months before the system is productive. Many businesses engage an implementation consultant. The Proper Tool has no formal onboarding process. Most builders produce their first quote in under 10 minutes with no setup required.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  simpro: string;
  simproState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "Built for residential construction", simpro: "No: field service focus", simproState: "no", tpt: "Yes: UK residential", tptState: "yes" },
  { feature: "AI scope generation from description", simpro: "No", simproState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings and PDFs", simpro: "Takeoffs add-on: partial/manual", simproState: "partial", tpt: "Yes: AI interpretation", tptState: "yes" },
  { feature: "UK residential construction pricing", simpro: "No universal UK cost book evidenced", simproState: "partial", tpt: "Yes: 31 UK trade categories", tptState: "yes" },
  { feature: "Scope completeness validation", simpro: "No", simproState: "no", tpt: "Yes: built-in expert review", tptState: "yes" },
  { feature: "Labour / materials / plant split", simpro: "Partial", simproState: "partial", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Multi-technician scheduling", simpro: "Yes (strong)", simproState: "yes", tpt: "No (quote-focused)", tptState: "no" },
  { feature: "Inventory management", simpro: "Yes", simproState: "yes", tpt: "No (quote-focused)", tptState: "no" },
  { feature: "Recurring service job management", simpro: "Excellent", simproState: "yes", tpt: "Not the focus", tptState: "no" },
  { feature: "Branded PDF quote output", simpro: "Yes", simproState: "yes", tpt: "Yes", tptState: "yes" },
  { feature: "Free trial available", simpro: "Guided personalised demo", simproState: "partial", tpt: "Yes: 14-day free trial", tptState: "yes" },
  { feature: "Time to first quote", simpro: "Weeks of implementation", simproState: "no", tpt: "Under 10 minutes", tptState: "yes" },
  { feature: "Starting price", simpro: "Request pricing", simproState: "partial", tpt: "£29/month (per account)", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
    title: "Built for field service, not residential construction",
    desc: "simPRO is designed for trades like electrical, plumbing, and HVAC that manage recurring service jobs with multiple technicians. Its quoting module is built around service job templates, not construction scopes of works. For builders doing extension, loft conversion, and structural alteration work, the fit is poor.",
  },
  {
    title: "Pricing database not calibrated for UK residential construction",
    desc: "simPRO provides supplier, catalogue and inventory functionality, but there is no evidence of a universal UK construction cost library included for residential builders.",
  },
  {
    title: "Steep learning curve and lengthy implementation",
    desc: "simPRO typically requires formal implementation support and several weeks of training before the system is productive. For small builders who want to start quoting quickly, this investment is a significant barrier. Contrast this with The Proper Tool, where most builders produce a first quote in under 10 minutes.",
  },
  {
    title: "Pricing requires a sales conversation",
    desc: "simPRO does not publish a standard UK price. Builders must request pricing and evaluate any implementation or add-on costs for their operation.",
  },
  {
    title: "No self-serve trial: demo-gated only",
    desc: "simPRO offers a guided, personalised demo rather than a self-serve trial. The Proper Tool offers a 14-day free trial with no credit card required.",
  },
];

const STAY_ON_SIMPRO = [
  "You manage a team of 10 or more field technicians with complex multi-job scheduling and dispatching needs.",
  "Your business model is recurring service contracts for electrical, plumbing, HVAC, or similar trades, not project-based residential construction.",
  "You have already completed the simPRO implementation and have established workflows in place.",
  "You need the full field service management suite: inventory, scheduling, invoicing, and reporting in one platform.",
];

const SWITCH_REASONS = [
  "You are a residential builder, not a field service business. simPRO is built for a fundamentally different type of work.",
  "You want a tool that generates construction scopes and pricing automatically from drawings or descriptions.",
  "You need to be up and quoting immediately, not after weeks of training and implementation.",
  "Your budget does not justify the cost of a full field service management platform when all you need is quoting.",
  "Construction-specific features (reading drawings, scope validation, UK residential pricing) matter more to you than scheduling and inventory.",
  "You want to try the software on real jobs before committing, without going through a sales demo process.",
];

const FAQS = [
  {
    q: "What is the best simPRO alternative for small builders?",
    a: "For small UK builders whose main need is fast, accurate construction quoting, The Proper Tool is the strongest simPRO alternative. simPRO is a full field service management platform designed for teams of 10+ with complex scheduling and inventory needs. The Proper Tool focuses specifically on construction quoting: AI-generated scopes from drawings or descriptions, a 31-category UK cost library, and a 14-day free trial from £29/month.",
  },
  {
    q: "Is simPRO good for residential builders?",
    a: "simPRO is primarily designed for field service businesses: electrical, plumbing, HVAC, and similar trades with recurring service contracts and multiple technicians. For residential builders doing extension and alteration work, it is generally more software than the job requires, and its pricing database is not calibrated for UK residential construction rates.",
  },
  {
    q: "How does simPRO pricing compare to The Proper Tool?",
    a: "simPRO does not publish a standard UK price. Request pricing and a guided personalised demo rather than a self-serve trial. The Proper Tool starts at £29 per month with a 14-day free trial.",
  },
  {
    q: "Can simPRO read construction drawings?",
    a: "simPRO's optional Takeoffs add-on supports plan upload, manual measurement and markup, plus symbol counting or recognition. This is partial drawing assistance, not full autonomous drawing understanding. The Proper Tool reads uploaded PDF drawings using AI.",
  },
  {
    q: "How long does it take to set up simPRO vs The Proper Tool?",
    a: "simPRO typically requires a formal implementation process that can take several weeks to months before the system is productive. The Proper Tool has no formal onboarding. Most builders produce their first quote in under 10 minutes with no setup required.",
  },
];

export default function SimproAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="simPRO Alternative for Small Builders: a Leaner Quoting-Only Option | The Proper Tool"
        description="Looking for a simPRO alternative for small UK builders? The Proper Tool is purpose-built for construction quoting: AI-powered, UK pricing, 14-day free trial from £29/mo."
        canonical="/simpro-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>simPRO Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              simPRO Alternative for Small Builders: a leaner quoting-only option
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              simPRO is a powerful field service platform, but for small UK builders who primarily need fast, accurate construction quoting, it's a lot of software for one specific need. Here's an honest comparison.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        {/* Why builders look */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why small builders look for a simPRO alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              simPRO is a well-built field service management platform with a large customer base. But small residential builders consistently run into the same mismatches:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature comparison table */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs simPRO: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">simPRO</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.simpro} state={row.simproState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
               Competitor pricing and feature information was checked on 19 September 2026. Prices and features may have changed. Verify directly with simPRO before making a purchasing decision.
            </p>
          </div>
        </section>

        {/* Pricing comparison */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              simPRO does not publish a standard UK price. Request pricing for the required plan, users, implementation and optional add-ons.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">simPRO</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Public UK price: request pricing</li>
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Guided personalised demo, not self-serve trial</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Implementation fees typically charged separately</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Weeks of setup before productive use</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month (per account)</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No implementation fees: first quote in under 10 minutes</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Who should stay on simPRO */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on simPRO</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              simPRO is the right tool for a specific type of business. If the following describes your operation, it may well be worth the investment:
            </p>
            <div className="space-y-3">
              {STAY_ON_SIMPRO.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why builders switch */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons builders switch from simPRO to The Proper Tool</h2>
            <p className="text-lg text-muted-foreground mb-8">The switch is typically from a complex platform to a focused quoting tool that does one thing very well.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our verdict */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Our verdict</h2>
            <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                simPRO and The Proper Tool serve fundamentally different needs. simPRO is a comprehensive field service management platform (and a capable one) for businesses that need to coordinate multiple technicians across recurring service jobs. If that describes your business, it is a reasonable choice.
              </p>
              <p>
                <strong className="text-foreground">For UK residential builders</strong> (sole traders, small firms, and growing SME contractors doing extension, loft conversion, and structural alteration work), simPRO is simply more software than the job requires. The quoting module is built around service job templates, not construction scopes, and the pricing database is not calibrated for UK residential construction.
              </p>
              <p>
                The Proper Tool is purpose-built for the quoting needs of UK residential builders. It reads drawings, generates scopes from descriptions, validates completeness, and produces professional branded PDFs. All from day one, without implementation consultants or training courses. Also considering Buildxact or EstimatorXpress? See the{" "}
                <Link href="/best/quoting-software-for-uk-builders" className="text-primary hover:underline">full comparison of all 6 tools →</Link>
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with simPRO before making a purchasing decision.
            </p>
          </div>
        </section>

        {/* Related */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all 6 quoting tools for UK builders" },
                { href: "/buildxact-alternative", label: "Buildxact Alternative UK" },
                { href: "/estimatorxpress-alternative", label: "EstimatorXpress Alternative" },
                { href: "/tradify-alternative", label: "Tradify Alternative" },
                { href: "/comparisons", label: "All Software Comparisons" },
                { href: "/for-small-builders", label: "The Proper Tool for Small Builders" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} The Proper Tool. Built for UK builders.</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
            <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
